/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  FormControl,
  TextInput,
  InputGroup,
  Loader,
  Switch,
  AutoSuggestCreatableProductCategory,
} from 'components'
import TextInputSearch from 'components/TextInput/TextInputSearch'

import { trimFirstSpace, statusFilter } from 'helpers/common'
import { LIMIT } from 'helpers/constants'

import type { ILoaderOption } from 'types'

const StockKeepingUnitForm = ({
  isEdit,
  previousCode,
}: {
  isEdit: boolean
  previousCode?: string
}): JSX.Element => {
  const [codeChecking, setCodeChecking] = useState<boolean>(false)
  const [nameChecking, setNameChecking] = useState<boolean>(false)
  const [brandChecking, setBrandChecking] = useState<boolean>(false)

  const { control, errors, setValue, trigger, getValues } = useFormContext()
  const { t } = useTranslation(['common', 'sku', 'productCategory'])

  const base = 'f-stock-keeping-unit__form'
  const addProductLimit = LIMIT.PRODUCT_CATEGORY

  const handleSearch = (item: ILoaderOption) => {
    setValue('categories', item, { shouldDirty: true })
  }

  const setOriginalValue = (
    <div style={{ display: 'none' }}>
      <Controller
        type='hidden'
        name='currentState'
        control={control}
        render={() => <TextInput />}
      />

      <Controller
        type='hidden'
        name='originalSkuCode'
        control={control}
        render={() => <TextInput />}
      />
    </div>
  )

  return (
    <>
      {setOriginalValue}

      <div className={`${base}__description-layout-status`}>
        <FormControl label={t('sku:sku.title')}>
          <Controller
            type='text'
            name='skuCode'
            control={control}
            render={(field) => (
              <TextInputSearch
                fullWidth
                placeholder={t('sku:insertSku.placeholder')}
                isLoading={codeChecking}
                value={field.value}
                selected={field.value}
                onChange={(e) => {
                  field.onChange(trimFirstSpace(e))
                }}
                onBlur={async () => {
                  if (
                    (previousCode &&
                      previousCode.trim() !== getValues('skuCode').trim()) ||
                    !isEdit
                  ) {
                    setCodeChecking(true)
                    setValue('skuCode', getValues('skuCode').trim())
                    setValue('currentState', 'skuCode')
                    await trigger('skuCode')
                    setValue('currentState', '')
                    setCodeChecking(false)
                  }
                }}
                isError={Boolean(errors.skuCode?.message)}
                message={errors.skuCode?.message}
                withIcon={codeChecking && <Loader />}
                maxLength={20}
                showIcon={false}
                ref={field.ref}
              />
            )}
          />
        </FormControl>

        <FormControl label={t('common:status.label')}>
          <Controller
            name='isActive'
            control={control}
            render={(fields) => (
              <Switch
                className={`${base}__switch`}
                label={statusFilter(fields.value)}
                checked={fields.value}
                onChange={(e) => fields.onChange(e.target.checked)}
              />
            )}
          />
        </FormControl>
      </div>

      <FormControl label={t('sku:productName.title')} required>
        <Controller
          type='text'
          name='name'
          control={control}
          render={(field) => (
            <TextInput
              value={field.value}
              onChange={(e) => {
                field.onChange(trimFirstSpace(e))
              }}
              onBlur={async () => {
                setNameChecking(true)
                setValue('name', getValues('name').trim())
                await trigger('name')
                setNameChecking(false)
              }}
              placeholder={t('sku:insertProductName.placeholder')}
              isError={field.value ? false : Boolean(errors.name?.message)}
              message={!field.value ? errors.name?.message : ''}
              withIcon={nameChecking && <Loader />}
              maxLength={100}
              ref={field.ref}
            />
          )}
        />
      </FormControl>

      <FormControl label={t('sku:brand.title')}>
        <Controller
          type='text'
          name='brand'
          control={control}
          render={(field) => (
            <TextInput
              value={field.value}
              onChange={(e) => {
                field.onChange(trimFirstSpace(e))
              }}
              onBlur={async () => {
                setBrandChecking(true)
                setValue('brand', getValues('brand').trim())
                await trigger('brand')
                setBrandChecking(false)
              }}
              placeholder={t('sku:insertBrand.placeholder')}
              isError={Boolean(errors.brand?.message)}
              message={errors.brand?.message}
              withIcon={brandChecking && <Loader />}
              maxLength={30}
              ref={field.ref}
            />
          )}
        />
      </FormControl>

      <FormControl
        label={t('sku:category.title')}
        recommend={t('productCategory:limit.autosuggest', {
          limit: addProductLimit,
        })}
      >
        <InputGroup variants='secondary'>
          <Controller
            type='text'
            name='categories'
            control={control}
            render={(field) => {
              const selected = isEdit ? field.value : undefined

              return (
                <AutoSuggestCreatableProductCategory
                  ref={field.ref}
                  limit={addProductLimit}
                  title={t('productCategory:duplicate.title')}
                  placeholder={t('sku:searchCategory.placeholder')}
                  notFoundText={t('productCategory:notfound.autosuggest')}
                  limitText={t('productCategory:limit.autosuggest', {
                    limit: addProductLimit,
                  })}
                  onChange={handleSearch}
                  selected={selected}
                  fullWidth
                  isSearchable
                  isMultiSelect
                />
              )
            }}
          />
        </InputGroup>
      </FormControl>
    </>
  )
}

export default StockKeepingUnitForm
